import { type CollabManager } from '@databases/collab-state-manager/collab-manager';
import { DATABASE_BE_STREAMING_PARTICIPANT } from '@databases/database-ai-first-creation-state';
import { presenceState } from '@databases/presence';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags';
import { type Transaction } from '@canvas/transaction';

import {
  getBackendAgentAddedEntryIds,
  getVisibleBackendAgentAddedEntryIds
} from '../backend-agent-entry-additions.ts';

export const PROGRESSIVE_ENTRY_REVEAL_DELAY_MS = 700;

const MAX_REVEAL_TICKS = 4;

type RevealState =
  | { kind: 'idle' }
  | {
      kind: 'revealing';
      activeEntryId: string;
      batchSize: number;
      hiddenEntryIds: ReadonlySet<string>;
      queuedEntryIds: readonly string[];
    };

const IDLE_REVEAL_STATE: RevealState = { kind: 'idle' };

interface PendingEntryAddition {
  collabManager: CollabManager;
  entryIds: readonly string[];
  resetKey: string;
}

interface UseProgressiveEntryRevealOptions {
  collabManager: CollabManager | undefined;
  entryIds: string[];
  onEligibleBackendAgentEntryAddition?: () => void;
  onLifecycleEvent?: (event: ProgressiveEntryRevealLifecycleEvent) => void;
  resetKey: string;
}

export type ProgressiveEntryRevealLifecycleEvent =
  | {
      action: 'started';
      entryCount: number;
      batchSize: number;
    }
  | {
      action: 'completed';
      entryCount: number;
      batchSize: number;
      durationMs: number;
    }
  | {
      action: 'cancelled';
      entryCount: number;
      batchSize: number;
      durationMs: number;
      cancellationReason: 'databaseChanged' | 'superseded' | 'unmounted';
    };

interface ActiveRevealLifecycle {
  entryCount: number;
  batchSize: number;
  startedAt: number;
  onLifecycleEvent?: (event: ProgressiveEntryRevealLifecycleEvent) => void;
}

export type ProgressiveEntryRevealResult =
  | { entryIds: string[]; kind: 'idle' }
  | { activeEntryId: string; entryIds: string[]; kind: 'revealing' };

/**
 * Stages only layout presentation while the complete database remains authoritative.
 */
export function useProgressiveEntryReveal({
  collabManager,
  entryIds,
  onEligibleBackendAgentEntryAddition,
  onLifecycleEvent,
  resetKey
}: UseProgressiveEntryRevealOptions): ProgressiveEntryRevealResult {
  const isDatabaseBEStreamingEnabled = getExperimentValue(
    'databaseBEStreaming',
    'enabled',
    { fireExperimentExposure: false }
  );
  const previousEntryIds = useRef(entryIds);
  const previousResetKey = useRef(resetKey);
  const previousTransactionEntryIds = useRef<readonly string[]>([]);
  const [pendingEntryAddition, setPendingEntryAddition] =
    useState<PendingEntryAddition>();
  const activeRevealLifecycle = useRef<ActiveRevealLifecycle>();
  const onLifecycleEventRef = useRef(onLifecycleEvent);
  onLifecycleEventRef.current = onLifecycleEvent;
  const [revealState, setRevealState] = useState<RevealState>(IDLE_REVEAL_STATE);

  const finishActiveReveal = useCallback(
    (
      action: 'completed' | 'cancelled',
      cancellationReason?: 'databaseChanged' | 'superseded' | 'unmounted'
    ) => {
      const activeReveal = activeRevealLifecycle.current;
      if (!activeReveal) {
        return;
      }

      activeRevealLifecycle.current = undefined;
      const lifecycleBase = {
        entryCount: activeReveal.entryCount,
        batchSize: activeReveal.batchSize,
        durationMs: Date.now() - activeReveal.startedAt
      };

      if (action === 'completed') {
        activeReveal.onLifecycleEvent?.({ action, ...lifecycleBase });
        return;
      }

      activeReveal.onLifecycleEvent?.({
        action,
        ...lifecycleBase,
        cancellationReason: cancellationReason ?? 'superseded'
      });
    },
    []
  );

  useLayoutEffect(() => {
    finishActiveReveal('cancelled', 'databaseChanged');
    setPendingEntryAddition(undefined);
    setRevealState(IDLE_REVEAL_STATE);

    if (!collabManager) {
      previousTransactionEntryIds.current = [];
      return;
    }

    previousTransactionEntryIds.current = collabManager.database.entries;
    const onAfterTransaction = (transaction: Transaction) => {
      const currentEntryIds = collabManager.database.entries;
      const addedEntryIds = getBackendAgentAddedEntryIds({
        currentEntryIds,
        previousEntryIds: previousTransactionEntryIds.current,
        transaction
      });
      previousTransactionEntryIds.current = currentEntryIds;

      if (addedEntryIds.length === 0) {
        return;
      }

      setPendingEntryAddition((pending) => {
        if (pending?.collabManager !== collabManager || pending.resetKey !== resetKey) {
          return { collabManager, entryIds: addedEntryIds, resetKey };
        }

        return {
          collabManager,
          entryIds: [...new Set([...pending.entryIds, ...addedEntryIds])],
          resetKey
        };
      });
    };

    collabManager.database.doc.on('afterTransaction', onAfterTransaction);
    return () => {
      collabManager.database.doc.off('afterTransaction', onAfterTransaction);
    };
  }, [collabManager, finishActiveReveal, resetKey]);

  useLayoutEffect(() => {
    if (isDatabaseBEStreamingEnabled) {
      return;
    }

    finishActiveReveal('cancelled', 'superseded');
    setRevealState(IDLE_REVEAL_STATE);
  }, [finishActiveReveal, isDatabaseBEStreamingEnabled]);

  useLayoutEffect(() => {
    const entryIdsChanged = previousEntryIds.current !== entryIds;
    const resetKeyChanged = previousResetKey.current !== resetKey;
    previousEntryIds.current = entryIds;
    previousResetKey.current = resetKey;

    if (resetKeyChanged) {
      setPendingEntryAddition(undefined);
      finishActiveReveal('cancelled', 'databaseChanged');
      setRevealState(IDLE_REVEAL_STATE);
      return;
    }

    if (!pendingEntryAddition) {
      if (entryIdsChanged) {
        finishActiveReveal('cancelled', 'superseded');
        setRevealState(IDLE_REVEAL_STATE);
      }
      return;
    }

    setPendingEntryAddition(undefined);
    if (
      pendingEntryAddition.collabManager !== collabManager ||
      pendingEntryAddition.resetKey !== resetKey
    ) {
      finishActiveReveal('cancelled', 'superseded');
      setRevealState(IDLE_REVEAL_STATE);
      return;
    }

    const visibleAddedEntryIds = getVisibleBackendAgentAddedEntryIds(
      pendingEntryAddition.entryIds,
      entryIds
    );

    if (visibleAddedEntryIds.length === 0) {
      finishActiveReveal('cancelled', 'superseded');
      setRevealState(IDLE_REVEAL_STATE);
      return;
    }

    onEligibleBackendAgentEntryAddition?.();
    if (!isDatabaseBEStreamingEnabled) {
      setRevealState(IDLE_REVEAL_STATE);
      return;
    }

    const queuedEntryIds = visibleAddedEntryIds.slice(1);
    const batchSize = Math.ceil(queuedEntryIds.length / MAX_REVEAL_TICKS);
    finishActiveReveal('cancelled', 'superseded');
    const lifecycleEventHandler = onLifecycleEventRef.current;
    activeRevealLifecycle.current = {
      entryCount: visibleAddedEntryIds.length,
      batchSize,
      startedAt: Date.now(),
      onLifecycleEvent: lifecycleEventHandler
    };
    lifecycleEventHandler?.({
      action: 'started',
      entryCount: visibleAddedEntryIds.length,
      batchSize
    });
    setRevealState({
      kind: 'revealing',
      activeEntryId: visibleAddedEntryIds[0],
      batchSize,
      hiddenEntryIds: new Set(queuedEntryIds),
      queuedEntryIds
    });
  }, [
    collabManager,
    entryIds,
    finishActiveReveal,
    isDatabaseBEStreamingEnabled,
    onEligibleBackendAgentEntryAddition,
    pendingEntryAddition,
    resetKey
  ]);

  // Each reveal state transition restarts the timer; switching to idle cancels it.
  useEffect(() => {
    if (revealState.kind === 'idle') {
      return;
    }

    const timeout = setTimeout(() => {
      if (revealState.queuedEntryIds.length === 0) {
        finishActiveReveal('completed');
        setRevealState(IDLE_REVEAL_STATE);
        return;
      }

      const revealedEntryIds = revealState.queuedEntryIds.slice(0, revealState.batchSize);
      const queuedEntryIds = revealState.queuedEntryIds.slice(revealState.batchSize);
      const hiddenEntryIds = new Set(revealState.hiddenEntryIds);

      for (const entryId of revealedEntryIds) {
        hiddenEntryIds.delete(entryId);
      }

      setRevealState({
        ...revealState,
        activeEntryId: revealedEntryIds[revealedEntryIds.length - 1],
        hiddenEntryIds,
        queuedEntryIds
      });
    }, PROGRESSIVE_ENTRY_REVEAL_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [finishActiveReveal, revealState]);

  useEffect(
    () => () => finishActiveReveal('cancelled', 'unmounted'),
    [finishActiveReveal]
  );

  const activeEntryId =
    revealState.kind === 'revealing' ? revealState.activeEntryId : undefined;
  useEffect(() => {
    if (activeEntryId === undefined) {
      return;
    }

    presenceState.addParticipant(
      { entryId: activeEntryId },
      DATABASE_BE_STREAMING_PARTICIPANT
    );
    return () => presenceState.removeParticipant(DATABASE_BE_STREAMING_PARTICIPANT);
  }, [activeEntryId]);

  return useMemo<ProgressiveEntryRevealResult>(() => {
    if (revealState.kind === 'idle') {
      return { kind: 'idle', entryIds };
    }

    return {
      activeEntryId: revealState.activeEntryId,
      kind: 'revealing',
      entryIds: entryIds.filter((entryId) => !revealState.hiddenEntryIds.has(entryId))
    };
  }, [entryIds, revealState]);
}
